import { useCallback, useMemo, useState } from "react";
import { CrossHairContext } from ".";
import { useSeekBar } from "../SeekBar";
import { PointerEventCallback } from "../../types/pointerEvent";
import { interpolateByRange } from "../../utils/interpolator";
import { usePointerMove } from "../../hooks/usePointerMove";
import { videoTimeToString } from "../../utils/time";
import { Handler } from "../../types/handler";
import { useRange } from "../Range";
import { useVideoSource } from "../VideoSource";
import { useMarkers } from "../Markers";

interface Props {
  children: React.ReactNode;
}

export const CrossHairProvider = ({ children }: Props) => {
  const { adjustedDuration, currentTimeOffset } = useRange();
  const { duration } = useVideoSource();
  const { markers } = useMarkers();

  const {
    isPointerInside,
    isPointerDown,
    activeHandler,
    hoverHandler,
    offset: seekBarOffset,
    width,
  } = useSeekBar();

  const [realSeconds, setRealSeconds] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [offset, setOffset] = useState(0);

  const reversedMarkers = [...markers].reverse();

  const currentMarker = reversedMarkers.find(
    (marker) => marker.time <= seconds + currentTimeOffset
  );

  const diff = offset - (currentMarker?.offset ?? 0);
  const isOnMarker = diff >= 0 && diff <= 6;

  const clipHandlerHover =
    hoverHandler === Handler.endClip || hoverHandler === Handler.startClip;
  const clipHandlerActive =
    activeHandler === Handler.startClip || activeHandler === Handler.endClip;

  const show =
    (isPointerDown || isPointerInside) &&
    !clipHandlerActive &&
    !clipHandlerHover;

  const pointerMoveHandler: PointerEventCallback = useCallback(
    ({ x }: PointerEvent) => {
      const newOffset = interpolateByRange(0, x - seekBarOffset, width);
      setRealSeconds(duration * (newOffset / width));
      setSeconds(adjustedDuration * (newOffset / width));
      setOffset(newOffset);
    },
    [adjustedDuration, duration, seekBarOffset, width]
  );
  usePointerMove(show, pointerMoveHandler);

  const value = useMemo(
    () => ({
      show,
      stringifiedTime: videoTimeToString({
        duration: adjustedDuration,
        currentTime: seconds,
      }).currentTime,
      seconds,
      isOnMarker,
      markerLabel: currentMarker?.label,
      realSeconds,
      offset,
    }),
    [
      show,
      adjustedDuration,
      seconds,
      isOnMarker,
      currentMarker?.label,
      realSeconds,
      offset,
    ]
  );

  return (
    <CrossHairContext.Provider value={value}>
      {children}
    </CrossHairContext.Provider>
  );
};
